import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { formatMinor } from '@/hooks/use-shifts';
import { useOpenScoFiscalHolds } from '@/hooks/use-sco-fiscal-holds';
import { useT } from '@/i18n/use-t';
import { ageText, cardAtRisk, cardStateText, fill, phaseText, when } from './fiscal-hold-labels';

/**
 * EAT-25 (EAT-16 slice P2) — every sale held for fiscal recovery on any
 * self-checkout lane of the tenant, oldest first, reached from the dashboard
 * tile. Each row opens the lane's page, where the held-sale panel shows the
 * commands and offers retry / void.
 */
export function FiscalHoldsListPage(): JSX.Element {
  const t = useT('admin_ui');
  const { data, isLoading, error } = useOpenScoFiscalHolds();
  const holds = data?.holds ?? [];

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">
          {t('terminals.fiscalHolds.title', 'Held fiscal sales')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'terminals.fiscalHolds.subtitle',
            'Paid sales a self-checkout lane could not fiscalise. Each one blocks its lane until it is retried or voided.',
          )}
        </p>
      </header>

      {isLoading && (
        <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      )}
      {error !== null && data === undefined && (
        <p className="text-sm text-destructive" role="alert">
          {t('terminals.fiscalHolds.loadFailed', 'Could not load the held sales.')}
        </p>
      )}
      {data !== undefined && holds.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="fiscal-holds-empty">
          {t('terminals.fiscalHolds.empty', 'No sale is held on any lane.')}
        </p>
      )}

      {holds.length > 0 && (
        <Table data-testid="fiscal-holds-table">
          <THead>
            <TR>
              <TH>{t('terminals.fiscalHolds.store', 'Store')}</TH>
              <TH>{t('terminals.fiscalHolds.terminal', 'Lane')}</TH>
              <TH>{t('terminals.fiscalHolds.since', 'Held since')}</TH>
              <TH>{t('terminals.fiscalHolds.amount', 'Amount')}</TH>
              <TH>{t('terminals.fiscalHold.cardState', 'Card payment')}</TH>
              <TH>{t('terminals.fiscalHold.stage', 'Stage')}</TH>
              <TH>{t('terminals.fiscalHolds.report', 'Lane report')}</TH>
            </TR>
          </THead>
          <TBody>
            {holds.map((hold) => (
              <TR
                key={`${hold.terminalId}:${hold.journalId}`}
                data-testid={`fiscal-holds-row-${hold.terminalCode}-${hold.journalId}`}
              >
                <TD>{hold.locationName ?? t('terminals.fiscalHolds.noStore', '— no store —')}</TD>
                <TD>
                  <Link className="underline" to={`/terminals/${hold.terminalId}`}>
                    {hold.terminalCode}
                  </Link>
                  <span className="ml-1 text-xs text-muted-foreground">{hold.terminalName}</span>
                </TD>
                <TD>{when(hold.since)}</TD>
                <TD className="tabular-nums">
                  {formatMinor(String(hold.totalMinor), hold.currency)}
                </TD>
                <TD className={cardAtRisk(hold.cardState) ? 'font-medium text-destructive' : ''}>
                  {cardStateText(t, hold.cardState)}
                </TD>
                <TD>
                  {phaseText(t, hold.phase)}
                  {hold.autoRetriesExhausted && (
                    <Badge variant="destructive" className="ml-1">
                      {t('terminals.fiscalHolds.needsDecision', 'needs a decision')}
                    </Badge>
                  )}
                  {hold.state === 'void_card_reversal_pending' && (
                    <Badge variant="destructive" className="ml-1">
                      {t('terminals.fiscalHolds.reversalPending', 'voided, card reversal pending')}
                    </Badge>
                  )}
                </TD>
                <TD>
                  {hold.stale ? (
                    <Badge variant="destructive">
                      {hold.reportAgeMs === null
                        ? t('terminals.fiscalHolds.neverReported', 'never reported')
                        : fill(t('terminals.fiscalHolds.stale', 'not reporting · {age} ago'), {
                            age: ageText(t, hold.reportAgeMs),
                          })}
                    </Badge>
                  ) : (
                    hold.reportAgeMs !== null &&
                    fill(t('terminals.fiscalHold.reportAge', '{age} ago'), {
                      age: ageText(t, hold.reportAgeMs),
                    })
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
